import React from 'react';
import { FD140 } from '../../../services/d204Api';
import { dateToInput, inputToDate, fmt, PRICING_METHOD } from '../helpers';

interface Tab1Props {
  formData: Partial<FD140>;
  names: Record<string, string>;
  isEditing: boolean;
  isFieldDisabled: (key: string) => boolean;
  getField: (key: keyof FD140) => FD140[keyof FD140];
  setField: (key: keyof FD140, value: FD140[keyof FD140]) => void;
  resolveSingleName: (nameKey: string, type: string, code: string) => void;
  getFieldError?: (field: string) => string | undefined;
}

const Tab1BasicInfo: React.FC<Tab1Props> = React.memo(({
  formData, names, isEditing, isFieldDisabled,
  getField, setField, resolveSingleName, getFieldError,
}) => {
  const E = (field: string) => {
    const err = getFieldError?.(field);
    return err ? <span style={{ color: '#ff4d4f', fontSize: 11, marginLeft: 6 }}>{err}</span> : null;
  };

  return (
    <>
      {/* Section 1: 基本資料 */}
      <div className="form-section odd">
        <span className="section-tag">基本資料</span>
        <div className="form-grid">
          <label>入庫單號</label>
          <input type="text" readOnly style={{ maxWidth: 140 }} value={getField('fd140_01') as string} data-contract-field="fd140_01" />

          <label>託工單號{E('fd140_11')}</label>
          <input type="text" style={{ maxWidth: 140 }}
            disabled={isFieldDisabled('fd140_11')}
            value={getField('fd140_11') as string}
            onChange={e => setField('fd140_11', e.target.value)} data-contract-field="fd140_11"
          />

          <label>入庫日期{E('fd140_12')}</label>
          <input type="date" style={{ maxWidth: 160 }}
            disabled={isFieldDisabled('fd140_12')}
            value={dateToInput(getField('fd140_12') as string)}
            onChange={e => setField('fd140_12', inputToDate(e.target.value) as string)} data-contract-field="fd140_12"
          />

          <label>加工廠{E('fd140_10')}</label>
          <div className="lookup-field">
            <input type="text" style={{ width: 100 }} maxLength={12}
              disabled={isFieldDisabled('fd140_10')}
              value={getField('fd140_10') as string}
              onChange={e => setField('fd140_10', e.target.value)}
              onBlur={() => resolveSingleName('processor', 'company', getField('fd140_10') as string)} data-contract-field="fd140_10"
            />
            <span className="lookup-name">{names.processor || '--'}</span>
          </div>

          <label>庫位{E('fd140_13')}</label>
          <div className="lookup-field">
            <input type="text" style={{ width: 80 }} maxLength={5}
              disabled={isFieldDisabled('fd140_13')}
              value={getField('fd140_13') as string}
              onChange={e => setField('fd140_13', e.target.value)}
              onBlur={() => resolveSingleName('warehouse', 'warehouse', getField('fd140_13') as string)} data-contract-field="fd140_13"
            />
            <span className="lookup-name">{names.warehouse || '--'}</span>
          </div>

          <label>領用單號</label>
          <input type="text" style={{ maxWidth: 140 }}
            disabled={isFieldDisabled('fd140_38')}
            value={getField('fd140_38') as string}
            onChange={e => setField('fd140_38', e.target.value)} data-contract-field="fd140_38"
          />

          <label>入布時間</label>
          <div className="lookup-field">
            <input type="text" readOnly style={{ width: 100 }} value={getField('fd140_43') as string} data-contract-field="fd140_43" />
            <span className="lookup-name">{names.entryPerson || '--'}</span>
          </div>

          <label>含樣品量縮率</label>
          <input type="number" style={{ maxWidth: 100 }}
            disabled={isFieldDisabled('fd140_14')}
            value={getField('fd140_14') as number || ''}
            onChange={e => setField('fd140_14', parseFloat(e.target.value) || 0)} data-contract-field="fd140_14"
          />

          <label>本次投入量</label>
          <input type="text" readOnly style={{ maxWidth: 140, textAlign: 'right' }}
            value={fmt(formData.fd140_201 as number)} data-contract-field="fd140_201"
          />

          <label>預計驗貨日期</label>
          <input type="text" readOnly style={{ maxWidth: 160 }}
            value={formData.fd140_44 ? dateToInput(formData.fd140_44 as unknown as string) : ''} data-contract-field="fd140_44"
          />

          <label>預計驗貨時間</label>
          <input type="text" readOnly style={{ maxWidth: 140 }}
            value={getField('fd140_45') as string} data-contract-field="fd140_45"
          />

          <label>異常原因</label>
          <input type="text" readOnly style={{ maxWidth: 80 }}
            value={getField('fd140_33') as string} data-contract-field="fd140_33"
          />

          <label>入庫類型</label>
          <div className="radio-group">
            <label>
              <input type="radio" name="fd140_25"
                disabled={isFieldDisabled('fd140_25')}
                checked={(getField('fd140_25') as number) === 1}
                onChange={() => setField('fd140_25', 1)} data-contract-field="fd140_25"
              />
              碼
            </label>
            <label>
              <input type="radio" name="fd140_25"
                disabled={isFieldDisabled('fd140_25')}
                checked={(getField('fd140_25') as number) === 2}
                onChange={() => setField('fd140_25', 2)} data-contract-field="fd140_25"
              />
              公斤
            </label>
          </div>
        </div>
      </div>

      {/* Section 2: 幣別與計費 */}
      <div className="form-section even">
        <span className="section-tag">幣別與計費</span>
        <div className="form-grid">
          <label>幣別</label>
          <div className="lookup-field">
            <input type="text" style={{ width: 80 }} maxLength={3}
              disabled={isFieldDisabled('fd140_36')}
              value={getField('fd140_36') as string}
              onChange={e => setField('fd140_36', e.target.value)}
              onBlur={() => resolveSingleName('currency', 'currency', getField('fd140_36') as string)} data-contract-field="fd140_36"
            />
            <span className="lookup-name">{names.currency || '--'}</span>
          </div>

          <label>匯率</label>
          <input type="number" style={{ maxWidth: 100 }} step="0.0001"
            disabled={isFieldDisabled('fd140_37')}
            value={getField('fd140_37') as number || ''}
            onChange={e => setField('fd140_37', parseFloat(e.target.value) || 0)} data-contract-field="fd140_37"
          />

          <label>計費方式</label>
          <div className="radio-group">
            {Object.entries(PRICING_METHOD).map(([val, label]) => (
              <label key={val}>
                <input type="radio" name="fd140_40"
                  disabled={isFieldDisabled('fd140_40')}
                  checked={(getField('fd140_40') as number) === Number(val)}
                  onChange={() => setField('fd140_40', Number(val))} data-contract-field="fd140_40"
                />
                {label}
              </label>
            ))}
          </div>

          <label>工繳總金額</label>
          <input type="text" readOnly style={{ maxWidth: 140, textAlign: 'right' }}
            value={fmt(formData.fd140_16)} data-contract-field="fd140_16"
          />

          <label>含稅金額</label>
          <input type="text" readOnly style={{ maxWidth: 140, textAlign: 'right' }}
            value={fmt(formData.fd140_161)} data-contract-field="fd140_161"
          />

          <label>稅額</label>
          <input type="text" readOnly style={{ maxWidth: 140, textAlign: 'right' }}
            value={fmt(formData.fd140_17)} data-contract-field="fd140_17"
          />

          <label>
            <span>三聯式發票</span>
          </label>
          <div className="checkbox-field">
            <input type="checkbox"
              disabled={isFieldDisabled('fd140_67')}
              checked={!!getField('fd140_67')}
              onChange={e => setField('fd140_67', e.target.checked)} data-contract-field="fd140_67"
            />
          </div>

          <label>
            <span>重修再入庫</span>
          </label>
          <div className="checkbox-field">
            <input type="checkbox"
              disabled={isFieldDisabled('fd140_63')}
              checked={!!getField('fd140_63')}
              onChange={e => setField('fd140_63', e.target.checked)} data-contract-field="fd140_63"
            />
          </div>

          <label>
            <span>停用工繳</span>
          </label>
          <div className="checkbox-field">
            <input type="checkbox"
              disabled={isFieldDisabled('fd140_64')}
              checked={!!getField('fd140_64')}
              onChange={e => setField('fd140_64', e.target.checked)} data-contract-field="fd140_64"
            />
          </div>
        </div>
      </div>
    </>
  );
});

Tab1BasicInfo.displayName = 'Tab1BasicInfo';
export default Tab1BasicInfo;
